/**
 * Insurer-letter upload helpers — Track G1.5.
 *
 * Responsibilities:
 *   1. downscaleImage(file, opts) — canvas-based image downscale so the
 *      sealed ciphertext fits within the bridge's 600 KB limit.
 *   2. sealAndUploadLetter(file, session) — seal the (possibly downscaled)
 *      file bytes with the Mac's public key and PUT them to the bridge.
 *
 * PDFs are not downscaled (no canvas path for multi-page PDFs). Patients
 * are advised to scan at lower DPI before uploading if a PDF is too large.
 *
 * Encryption: same sealedbox construction as form submissions (sealSubmission
 * in bridgeClient.js). Only the sealed ciphertext leaves the browser.
 */

import { sealSubmission, getBridgeSession } from "./bridgeClient";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

// Target: ≤ 450 KB plaintext so the sealed ciphertext sits well under 600 KB.
const TARGET_PLAINTEXT_BYTES = 450 * 1024;
// Max long edge in pixels for downscaled images.
const MAX_LONG_EDGE_PX = 1600;
// JPEG quality for downscaled output.
const JPEG_QUALITY = 0.80;

/**
 * Downscale an image File to fit within TARGET_PLAINTEXT_BYTES.
 *
 * Returns a Uint8Array of JPEG bytes. Uses a canvas to redraw at a reduced
 * size. Progressively halves JPEG quality if the first attempt still exceeds
 * the limit (rarely needed in practice — 1600 px / q=0.80 is already small).
 *
 * Non-image files (application/pdf) are returned as-is (Uint8Array of the
 * original bytes). The caller handles the size-check.
 *
 * @param {File} file
 * @returns {Promise<{ bytes: Uint8Array, mime: string, filename: string }>}
 */
export async function prepareFileForUpload(
  file: File,
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  const mime = file.type || "application/octet-stream";

  // PDFs — read raw, no downscale.
  if (mime === "application/pdf" || !mime.startsWith("image/")) {
    const bytes = await readFileAsBytes(file);
    return { bytes, mime, filename: file.name };
  }

  // Images — load into a canvas and downscale.
  const imageBytes = await downscaleImage(file);
  return { bytes: imageBytes, mime: "image/jpeg", filename: toJpegFilename(file.name) };
}

/**
 * Downscale an image to MAX_LONG_EDGE_PX and JPEG_QUALITY.
 * Returns Uint8Array of JPEG bytes.
 */
async function downscaleImage(file: File): Promise<Uint8Array> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { w, h } = computeDimensions(img.naturalWidth, img.naturalHeight, MAX_LONG_EDGE_PX);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, w, h);

    // First attempt at JPEG_QUALITY.
    let blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
    // If still too large, progressively lower quality.
    let quality = JPEG_QUALITY;
    while (blob.size > TARGET_PLAINTEXT_BYTES && quality > 0.3) {
      quality = Math.max(0.3, quality - 0.15);
      blob = await canvasToBlob(canvas, "image/jpeg", quality);
    }
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

function computeDimensions(w: number, h: number, maxLongEdge: number): { w: number; h: number } {
  const longEdge = Math.max(w, h);
  if (longEdge <= maxLongEdge) return { w, h };
  const scale = maxLongEdge / longEdge;
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error("canvas.toBlob returned null")),
      type,
      quality,
    );
  });
}

function readFileAsBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: any) => resolve(new Uint8Array(e.target.result));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function toJpegFilename(name: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base}.jpg`;
}

/**
 * Seal the file bytes under the Mac's public key and PUT to the bridge.
 *
 * `session` must carry { sessionToken, assignmentId, pubkeyB64 }.
 *
 * Throws on size excess or network/bridge errors.
 *
 * @param {File} file  The raw file the patient selected.
 * @param {object} [sessionOverride]  If provided, use this instead of getBridgeSession().
 * @returns {Promise<{ letter_id: string, received_at: string }>}
 */
interface BridgeSession {
  sessionToken?: string;
  assignmentId?: string;
  pubkeyB64?: string;
  [key: string]: unknown;
}

export async function sealAndUploadLetter(
  file: File,
  sessionOverride?: BridgeSession | null,
): Promise<{ letter_id: string; received_at: string }> {
  // `any` after the manual guard below: the session comes from getBridgeSession()
  // (untyped .js) or an override, and is guarded by hand for the three keys used.
  const session: any = sessionOverride || getBridgeSession();
  if (!session?.sessionToken || !session?.assignmentId || !session?.pubkeyB64) {
    throw new Error("No active bridge session — cannot upload letter");
  }

  const { bytes, mime, filename } = await prepareFileForUpload(file);

  // Seal the raw file bytes under the Mac's public key.
  // sealSubmission expects a JSON payload; for binary files we encode as base64
  // then wrap in a thin envelope so the Mac knows how to reassemble the bytes.
  // The Mac's _decrypt_raw in bridge_sync.py unwraps this and re-encodes as
  // plain file bytes before forwarding to the intake endpoint.
  //
  // Wire shape: { __letter__: true, b64: <base64 of file bytes>, mime, filename }
  // This lets the Mac distinguish a binary letter upload from a form submission.
  const letterPayload = {
    __letter__: true,
    b64: bytesToBase64(bytes),
    mime,
    filename,
  };

  const ciphertextB64 = sealSubmission(letterPayload, session.pubkeyB64);

  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(session.assignmentId)}/letter-upload`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Patient-Token": session.sessionToken,
      },
      credentials: "omit",
      body: JSON.stringify({
        file_b64: ciphertextB64,
        mime,
        original_filename: filename,
      }),
    },
  );

  if (!resp.ok) {
    const body = await resp.text();
    if (resp.status === 413) {
      throw new Error(
        "The file is too large to upload. Please try a lower-resolution photo or scan."
      );
    }
    throw new Error(`Upload failed (${resp.status}): ${body.slice(0, 200)}`);
  }
  return resp.json();
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
